// @flow
/*
 *  Copyright 2017 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */
import AppsUtil from './AppsUtil';
import CompatibilityUtil from './CompatibilityUtil';
import {buttonData, appButtonConfig, actionIds} from '../constants/appsConstants';
import thorUtil from './wam/ThorUtil';
import {thorAppsStatesCategory} from '../constants/wamConstants';
import {AppAction} from '@ccxi/ccxi-apps/src/stores/app';

type ButtonDetails = {
	[key: string]: string,
	actionType: {
		[key: string]: ?string | boolean,
		icon?: ?string
	}
};

class CTAUtil {
	config: Object;

	// Singleton
	// static instance;
	// config fields - type,sapCode,entitled,displayName,hasThor
	// do not change order of isappsupported as we call ctautil from modelparser while creating app model and appsFFCProductList is not filled till that time and calling that method will break the code, that method will be called in case of pdp only.
	constructor(config: Object) {
		/*
		if (this.instance) {
			return this.instance;
		}
		this.instance = this;
		*/
		this.config = config;
	}

	/**
	 * Initializes Apps Util
	 *
	 */
	init() {

	}

	isKcccThorWorkflow() {
		return this.config.sapCode === 'KCCC' && AppsUtil.hasThor;
	}

	isWorkflowInline() {
		const isInlineWorkflow = (this.config.type === 'desktop') && this.config.hasThor && thorUtil.getWAMClient().isConnected();
		if (this.config) {
			if (CompatibilityUtil.isOSDeprecated()) {
				return false;
			}
			const {productFromThor} = thorUtil.getAppFromThorAndFFC(AppsUtil.appsFFCProductList, this.config.sapCode);
			if (!productFromThor || (productFromThor.stateCategory && productFromThor.stateCategory === thorAppsStatesCategory.incompatibleWithCheckprevious)) {
				return false;
			}
		}
		if (!AppsUtil.isUserEnterpriseUser()) {
			return isInlineWorkflow;
		}
		return isInlineWorkflow && this.config.entitled; // For enterprise users, he should be entitled .
	}

	getButtonAriaLabel(linkText: string) {
		const buttonText = linkText;
		if (['ios', 'android', 'windows'].indexOf(linkText.toLowerCase()) !== -1) {
			return ['Get', this.config.displayName, 'for', buttonText].join(' ');
		}
		if (['learn more'].indexOf(linkText.toLowerCase()) !== -1) {
			return [buttonText, this.config.displayName].join(' about ');
		}
		return [buttonText, this.config.displayName].join(' ');
	}

	getButtonLinkText(linkText: string) {
		return AppsUtil.intl.formatMessage({id: linkText});
	}

	isEnterpriseUserAndTrailApps() {
		return AppsUtil.isUserEnterpriseUser() && (this.config && !this.config.entitled);
	}

	getButtonList(isPDP: ?boolean, getAllBtnList: ?boolean) {
		let tileButtonData: ButtonDetails[] = [];
		const isMobileOrTab = AppsUtil.isMobileOrTab();
		const isPDPOnMobileOrTab = isPDP && isMobileOrTab;
		const isSmallBreakPoint = window.matchMedia && !window.matchMedia('(min-width: 768px)').matches;
		const isDesktopType = this.config.type.toLowerCase() === 'desktop';
		const buttonConfig = this.config.type === 'desktop' ? appButtonConfig[this.config.sapCode] : appButtonConfig[this.config.type];
		if (buttonConfig) {
			const tileButtons = buttonConfig[this.config.entitled ? 'inPlan' : 'notInPlan'].tile;
			tileButtons.forEach((button) => {
				const buttonDetails: ButtonDetails = Object.assign({}, buttonData[button]);
				buttonDetails.actionType = {
					component: 'button',
					variant: isPDP ? 'cta' : 'primary',
					quiet: !isPDP
				};
				buttonDetails.actionId = button;
				buttonDetails.localizedText = this.getButtonLinkText(buttonDetails.buttonText);
				if (isDesktopType) {
					if ((AppsUtil.isUserEnterpriseUser() && isPDP !== true) || getAllBtnList) {
						if (this.config.entitled) {
							if (this.config.hasThor && !this.isKcccThorWorkflow()) {
								if (buttonDetails.buttonName === 'install') {
									if (AppsUtil.isMobileOrTab()) {
										if (AppsUtil.hasFeature('temp_send_link_for_desktop_apps')) {
											buttonDetails.localizedText = this.getButtonLinkText('apps.tile.sendlink');
										}
										buttonDetails.actionId = actionIds.sendLink;
									}
									tileButtonData.push(new AppAction(buttonDetails));
								}
							}
							else {
								if (buttonDetails.buttonName === 'download'
									&& !(AppsUtil.isSubstanceDesktopApp(this.config.sapCode) && !AppsUtil.isMobileOrTab())) {
									if (AppsUtil.isMobileOrTab()) {
										if (AppsUtil.hasFeature('temp_send_link_for_desktop_apps')) {
											buttonDetails.localizedText = this.getButtonLinkText('apps.tile.sendlink');
										}
										buttonDetails.actionId = actionIds.sendLink;
									}
									tileButtonData.push(new AppAction(buttonDetails));
								}
							}
						}
					}
					else {
						switch (buttonDetails.buttonName) {
							case 'install':
								if (this.config.hasThor && (!isPDP || getAllBtnList || (isPDP && CompatibilityUtil.isAppSupported(this.config.sapCode)))) {
									if (this.isKcccThorWorkflow()) {
										break;
									}
									if (AppsUtil.isMobileOrTab()) {
										if (AppsUtil.hasFeature('temp_send_link_for_desktop_apps')) {
											buttonDetails.localizedText = this.getButtonLinkText('apps.tile.sendlink');
										}
										buttonDetails.actionId = actionIds.sendLink;
									}
									tileButtonData.push(new AppAction(buttonDetails));
								}
								break;
							case 'download':
								if ((!this.config.hasThor || this.isKcccThorWorkflow()) && (!isPDP || getAllBtnList || (isPDP && (isMobileOrTab || CompatibilityUtil.isAppSupported(this.config.sapCode)))) && !(AppsUtil.isSubstanceDesktopApp(this.config.sapCode) && !AppsUtil.isMobileOrTab())) {
									if (isPDPOnMobileOrTab && isSmallBreakPoint) {
										buttonDetails.actionType = {
											...buttonDetails.actionType,
											icon: 'Download',
											iconOnly: true,
											variant: 'action',
											quiet: true,
										};
									}
									if (AppsUtil.isMobileOrTab()) {
										if (AppsUtil.hasFeature('temp_send_link_for_desktop_apps')) {
											buttonDetails.localizedText = this.getButtonLinkText('apps.tile.sendlink');
										}
										buttonDetails.actionId = actionIds.sendLink;
									}
									tileButtonData.push(new AppAction(buttonDetails));
								}
								break;
							case 'try':
								if (!isPDP || getAllBtnList || (isPDP && (isMobileOrTab || CompatibilityUtil.isAppSupported(this.config.sapCode)) && !AppsUtil.isUserEnterpriseUser())) {
									buttonDetails.actionType = {
										component: isPDPOnMobileOrTab ? 'dropdown' : 'button',
										variant: 'primary'
									};
									tileButtonData.push(new AppAction(buttonDetails));
								}
								break;
							case 'buy':
								if (!(isPDP && AppsUtil.isUserEnterpriseUser())) {
									tileButtonData.push(new AppAction(buttonDetails));
								}
						}
					}
				}
				else {
					if (buttonDetails.buttonName !== 'learn more' && buttonDetails.buttonName !== 'windows') {
						buttonDetails.actionType = {
							component: 'button',
							variant: isPDP ? 'cta' : 'primary',
							quiet: true,
							icon: null,
							iconOnly: (isPDPOnMobileOrTab && isSmallBreakPoint)
						};
						if (isPDPOnMobileOrTab && isSmallBreakPoint) {
							buttonDetails.actionType.variant = 'action';
							buttonDetails.actionType.icon = this.config.type === 'web' ? 'LinkOut' : 'Download';
						}
						// For mobile app on Web, filter out
						// button whose actionId = launch-mobile.
						// This button should only be shown for mobile apps
						// on mobile device.
						if (!isMobileOrTab && buttonDetails.actionId !== actionIds.launchMobile) {
							tileButtonData.push(new AppAction(buttonDetails));
						}
						else if (isMobileOrTab && buttonDetails.actionId !== actionIds.sendLink) {
							if (buttonDetails.actionId !== actionIds.launchMobile) {
								tileButtonData.push(new AppAction(buttonDetails));
							}
							else {
								tileButtonData.push(new AppAction({
									...buttonDetails,
									localizedText: this.getButtonLinkText('apps.tile.getformobile'),
								}));
							}
						}
					}
				}
			});
		}

		/* add new buttons */
		if (isDesktopType) {
			// Permission maybe required CTA for substance
			// desktop apps in kaizen workflow
			if (((this.isEnterpriseUserAndTrailApps() && isPDP) || getAllBtnList)
				|| (AppsUtil.shouldShowPermissionReqdForSubstance(this.config.sapCode)
				&& isPDP)) {
				tileButtonData.unshift({
					actionId: actionIds.enterprise,
					localizedText: this.getButtonLinkText(buttonData[actionIds.enterprise].buttonText),
					actionType: {
						component: 'button',
						variant: 'primary',
						quiet: true,
						icon: 'error',
						iconOnly: (isPDPOnMobileOrTab && isSmallBreakPoint)
					}
				});
			}
			if (getAllBtnList || (isPDP && !isMobileOrTab && !CompatibilityUtil.isAppSupported(this.config.sapCode)) && !this.isEnterpriseUserAndTrailApps() && !AppsUtil.shouldShowPermissionReqdForSubstance(this.config.sapCode)) {
				const btnConfig: Object = {
					actionId: actionIds.notCompatible,
					localizedText: this.getButtonLinkText(buttonData[actionIds.notCompatible].buttonText),
					actionType: {
						component: 'button',
						variant: 'primary',
						quiet: true
					}
				};
				if (getAllBtnList || (isPDP && CompatibilityUtil.isAnyOlderVersionSupported(this.config.sapCode)) && !this.isEnterpriseUserAndTrailApps()) {
					if (!this.config.entitled) {
						btnConfig.actionId = actionIds.tryOldVersion;
						btnConfig.localizedText = this.getButtonLinkText(buttonData[actionIds.tryOldVersion].buttonText);
					}
					else if (this.config.hasThor) {
						btnConfig.actionId = actionIds.installOldVersion;
						btnConfig.localizedText = this.getButtonLinkText(buttonData[actionIds.installOldVersion].buttonText);
					}
					else {
						btnConfig.actionId = actionIds.downloadOldVersion;
						btnConfig.localizedText = this.getButtonLinkText(buttonData[actionIds.downloadOldVersion].buttonText);
					}
				}
				else {
					btnConfig.actionType.icon = 'error';
				}

				tileButtonData.unshift(btnConfig);
			}
		}

		tileButtonData = (isPDP || getAllBtnList) ? tileButtonData.concat(this.getDropDownList(isPDP,getAllBtnList)) : tileButtonData;

		// keep this to add in actionlist which should have all the possible buttons
		if (getAllBtnList && isDesktopType && !isMobileOrTab) {
			tileButtonData.push({
				actionId: actionIds.notCompatible,
				localizedText: this.getButtonLinkText(buttonData[actionIds.notCompatible].buttonText),
				actionType: {
					component: 'button',
					variant: 'primary',
					quiet: true,
					icon: 'error'
				}
			});
		}

		if (!isPDP && !this.isEnterpriseUserAndTrailApps()) {
			tileButtonData.unshift(new AppAction({
				actionId: actionIds.learnMore,
				actionType: {
					component: 'button',
					variant: 'secondary',
					quiet: true
				},
				localizedText: this.getButtonLinkText(buttonData[actionIds.learnMore].buttonText)
			}));
		}

		if (tileButtonData.length === 1 && tileButtonData[0].actionId === actionIds.try && isPDP) {
			tileButtonData[0].actionType.component = 'button';
			tileButtonData[0].actionType.variant = 'cta';
			tileButtonData[0].actionType.quiet = false;
		}
		// Using outline for primary button
		if (!getAllBtnList && !isPDP && tileButtonData.length > 0) {
			tileButtonData[tileButtonData.length - 1].actionType.quiet = false;
		}
		return tileButtonData;
	}

	getDropDownList(isPDP: ?boolean, getAllBtnList: ?boolean) {
		const isDesktopType = this.config.type.toLowerCase() === 'desktop';
		const dropDownBtnList = [];

		if ((AppsUtil.isMobileOrTab() && !(this.isEnterpriseUserAndTrailApps() && isPDP)) || (this.isEnterpriseUserAndTrailApps() && !(isPDP || getAllBtnList))) {
			return dropDownBtnList;
		}
		if (getAllBtnList || (isPDP && isDesktopType && (this.isEnterpriseUserAndTrailApps() || !CompatibilityUtil.isAppSupported(this.config.sapCode)) && !AppsUtil.shouldShowPermissionReqdForSubstance(this.config.sapCode))) {
			dropDownBtnList.push({
				actionId: actionIds.learnMorePDP,
				actionType: {
					component: 'dropdown'
				},
				localizedText: this.getButtonLinkText(buttonData[actionIds.learnMore].buttonText)
			});
		}
		return dropDownBtnList;
	}
}

export default CTAUtil;
